// BulkProductImport.tsx - Fixed version to prevent infinite loops
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Steps, Progress, Alert, Typography, Upload, Space, message } from 'antd';
import { UploadOutlined, FileExcelOutlined, InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { parseCSV, generateCSVTemplate, validateMapping } from '../../utils/csvUtils';
import FieldMapperComponent, { FieldDefinition } from '../../components/FieldMapperComponent';
import DrawerComponent from '../../components/drawer';
import { bulkImportProducts } from '../../services/productService';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
// Added /* @ts-ignore */ to suppress TypeScript error if types aren't installed yet
/* @ts-ignore */
import Papa from 'papaparse';
import logger from '../../utils/logger';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

// Define styled components for the UI
const StepContent = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const UploadArea = styled.div`
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background: #fafafa;
  cursor: pointer;
  transition: border-color 0.3s;
  margin-bottom: 20px;

  &:hover {
    border-color: #1890ff;
  }
`;

const StyledImportButton = styled(Button)`
  height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  font-weight: 500;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  
  &:hover {
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
  
  .anticon {
    font-size: 16px;
  }
`;


const CompletionContent = styled.div`
  text-align: center;
  padding: 30px 20px;

  .success-icon {
    font-size: 48px;
    color: #52c41a;
    margin-bottom: 16px;
  }
`;

const StepsContainer = styled.div`
  margin-bottom: 24px;
`;

// Update the ActionButtons styled component
const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background-color: white;
  border-top: 1px solid #f0f0f0;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
`;

// Default field definitions for products
const DEFAULT_PRODUCT_FIELDS: FieldDefinition[] = [
  {
    name: 'name',
    label: 'Product Name',
    required: true,
    similarNames: ['product name', 'product', 'item name', 'item']
  },
  {
    name: 'price',
    label: 'Price',
    required: true,
    similarNames: ['cost', 'amount', 'value']
  },
  {
    name: 'category',
    label: 'Category',
    similarNames: ['product category', 'type', 'product type']
  },
  {
    name: 'description',
    label: 'Description',
    similarNames: ['product description', 'details', 'info']
  },
  {
    name: 'unit',
    label: 'Unit',
    similarNames: ['measure', 'unit type', 'quantity type']
  },
  {
    name: 'currency',
    label: 'Currency',
    similarNames: ['currency code', 'currency type']
  },
  {
    name: 'gst',
    label: 'GST',
    similarNames: ['tax', 'gst rate', 'tax rate', 'gst%', 'tax%', 'gst percentage']
  }
];

/**
 * BulkProductImport component handles the entire workflow for importing products from CSV
 */
const BulkProductImport: React.FC = () => {
  // State variables
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<{ headers: string[]; data: any[] }>({ headers: [], data: [] });
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<{ total: number; successful: number; failed: number }>({
    total: 0,
    successful: 0,
    failed: 0
  });
  const [formCloseModel, setFormCloseModel] = useState(false);
  
  // Reference to track if fields are already initialized
  const fieldsInitializedRef = useRef(false);

  // Get custom field definitions from Redux store
  const customFieldDefs = useSelector((state: any) =>
    state?.user?.userPreferences?.customForm || []
  );

  // Combine default fields with custom fields
  const [productFields, setProductFields] = useState<FieldDefinition[]>(DEFAULT_PRODUCT_FIELDS);

  // Effect to merge custom fields with default fields - FIX: use ref to prevent multiple executions
  useEffect(() => {
    // Skip if already initialized
    if (fieldsInitializedRef.current) return;
    
    logger.log('Initializing product fields with custom fields');
    fieldsInitializedRef.current = true;
    
    // Filter out custom fields that might overlap with default fields
    const defaultFieldNames = DEFAULT_PRODUCT_FIELDS.map(field => field.name);
    const filteredCustomFields = customFieldDefs
      .filter((field: any) => !defaultFieldNames.includes(field.name))
      .map((field: any) => ({
        name: field.name,
        label: field.label || field.name,
        required: field.isRequired || false,
        type: field.type || 'text'
      }));

    setProductFields([...DEFAULT_PRODUCT_FIELDS, ...filteredCustomFields]);
  }, [customFieldDefs]);

  // Reset states when drawer is closed
  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      setCsvFile(null);
      setCsvData({ headers: [], data: [] });
      setFieldMapping({});
      setUploadProgress(0);
      setIsUploading(false);
      setError(null);
      fieldsInitializedRef.current = false; // Reset the initialization ref
    }
  }, [visible]);

  // Generate template CSV headers based on product fields
  const generateTemplate = useCallback(() => {
    const headers = productFields.map(field => field.label || field.name);
    generateCSVTemplate(headers, 'product-import-template.csv');
  }, [productFields]);

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      logger.log('Parsing CSV file:', file.name);
      const result = await parseCSV(file);
      logger.log('CSV parsed successfully with headers:', result.headers);
      
      setCsvFile(file);
      setCsvData(result);
      
      if (result.headers.length > 0) {
        setCurrentStep(1); // Move to mapping step
      } else {
        setError('The CSV file has no headers. Please check the file format.');
      }
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error('CSV parsing error:', error);
      setError('Failed to parse CSV file. Please check the format.');
      return false;
    }
  };

  // Handle field mapping changes
  const handleMappingChange = (mappings: Record<string, string>) => {
    logger.log('Field mapping updated:', mappings);
    setFieldMapping(mappings);
    setError(null); // Clear any previous mapping errors
  };

  // Validate mappings before import
  const validateBeforeImport = () => {
    const requiredFields = productFields
      .filter(field => field.required)
      .map(field => field.name);
    
    const { isValid, missingFields } = validateMapping(fieldMapping, requiredFields);
    
    if (!isValid) {
      setError(`Required fields missing: ${missingFields.join(', ')}`);
      return false;
    }
    
    return true;
  };

  // Start the import process
  const startImport = async () => {
    if (!csvFile || !validateBeforeImport()) {
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      logger.log('Starting import process');
      
      // Prepare form data
      const formData = new FormData();
      formData.append('csv', csvFile);
      
      // Add mappings to form data
      Object.entries(fieldMapping).forEach(([fieldName, csvHeader]) => {
        if (csvHeader && csvHeader !== "Don't import") {
          formData.append(fieldName, csvHeader);
        }
      });

      // Make the API call
      const response = await bulkImportProducts(formData);
      logger.log('Import successful, response:', response.data);
      
      // Process results
      const results = response.data;
      setImportResults({
        total: results.total || csvData.data.length,
        successful: results.successful || results.total || csvData.data.length,
        failed: results.failed || 0
      });
      
      // Move to success step
      setCurrentStep(2);
      message.success('Products imported successfully!');
    } catch (error: any) {
      console.error('Import error:', error);
      setError(error.response?.data?.message || 'Failed to import products. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(100); // Ensure progress completes even if there was an error
    }
  };

  // Handle next step button
  const handleNext = () => {
    if (currentStep === 0 && !csvFile) {
      setError('Please upload a CSV file first.');
      return;
    }

    if (currentStep === 1 && !validateBeforeImport()) {
      return;
    }

    if (currentStep === 1) {
      startImport();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  // Handle previous step button
  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    setError(null);
  };

  // Handle drawer close with confirmation
  const handleCloseDrawer = () => {
    if (csvFile && currentStep > 0 && !isUploading) {
      setFormCloseModel(true);
    } else {
      setVisible(false);
    }
  };

  // Render different content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Upload file
        return (
          <StepContent>
            <Paragraph>
              Upload a CSV file with your product data. The file should have headers that can be mapped to product fields.
            </Paragraph>
            
            <UploadArea onClick={() => document.getElementById('csv-upload-input')?.click()}>
              <input
                id="csv-upload-input"
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file);
                  }
                }}
              />
              <FileExcelOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
              <Paragraph>
                Click or drag a CSV file to this area to upload
              </Paragraph>
              <Button icon={<UploadOutlined />}>Select File</Button>
            </UploadArea>
            
            <Paragraph type="secondary">
              <InfoCircleOutlined /> Need a template? 
              <Button 
                type="link" 
                onClick={generateTemplate}
              >
                Download CSV template
              </Button>
            </Paragraph>
            
            {csvFile && (
              <Alert
                message="File Selected"
                description={`File: ${csvFile.name} (${(csvFile.size / 1024).toFixed(2)} KB)`}
                type="success"
                showIcon
              />
            )}
          </StepContent>
        );
      
      case 1: // Map fields
        return (
          <StepContent>
            <Paragraph>
              Map the columns from your CSV file to the corresponding product fields. Required fields are marked with an asterisk (*).
            </Paragraph>
            
            <FieldMapperComponent 
              sourceFields={csvData.headers}
              targetFields={productFields}
              onChange={handleMappingChange}
              initialMappings={fieldMapping}
            />
            
            {/* Preview data sample */}
            {csvData.data.length > 0 && (
              <div>
                <Title level={5}>Data Preview</Title>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {csvData.headers.map(header => (
                          <th key={header} style={{ border: '1px solid #f0f0f0', padding: '8px', background: '#fafafa' }}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.data.slice(0, 3).map((row, index) => (
                        <tr key={index}>
                          {csvData.headers.map(header => (
                            <td key={`${index}-${header}`} style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                              {row[header]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvData.data.length > 3 && (
                  <Text type="secondary">Showing 3 of {csvData.data.length} rows</Text>
                )}
              </div>
            )}
          </StepContent>
        );
      
      case 2: // Success
        return (
          <StepContent>
            <CompletionContent>
              <CheckCircleOutlined className="success-icon" />
              <Title level={3}>Import Complete!</Title>
              <Paragraph>
                Successfully imported products from your CSV file.
              </Paragraph>
              
              <div style={{ margin: '24px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                  <div>
                    <Text strong>Total:</Text>
                    <div>{importResults.total}</div>
                  </div>
                  <div>
                    <Text strong style={{ color: '#52c41a' }}>Successful:</Text>
                    <div>{importResults.successful}</div>
                  </div>
                  {importResults.failed > 0 && (
                    <div>
                      <Text strong style={{ color: '#ff4d4f' }}>Failed:</Text>
                      <div>{importResults.failed}</div>
                    </div>
                  )}
                </div>
              </div>
              
              <Button type="primary" onClick={() => setVisible(false)}>
                Close
              </Button>
            </CompletionContent>
          </StepContent>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <StyledImportButton
  icon={<UploadOutlined />}
  onClick={() => setVisible(true)}
>
  Bulk Import
</StyledImportButton>
  
    

      <DrawerComponent
  openWithHeader={visible}
  setOpenWithHeader={handleCloseDrawer}
  drawerTitle="Bulk Import Products"
  size="md"
>
  <div style={{ 
    padding: '0 16px', 
    height: 'calc(100vh - 120px)', 
    display: 'flex', 
    flexDirection: 'column', 
    overflow: 'hidden' 
  }}>
    <div style={{ overflowY: 'auto', flex: '1 1 auto' }}>
      <StepsContainer>
        <Steps current={currentStep}>
          <Step title="Upload" description="Select CSV file" />
          <Step title="Map Fields" description="Match columns" />
          <Step title="Complete" description="Review results" />
        </Steps>
      </StepsContainer>
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      
      {renderStepContent()}
      
      {isUploading && (
        <div style={{ marginBottom: '24px' }}>
          <Progress percent={uploadProgress} status="active" />
          <Text>Importing products... Please wait</Text>
        </div>
      )}
    </div>
    
    <ActionButtons>
  <div>
    {currentStep > 0 && currentStep < 2 && (
      <Button 
        onClick={handlePrevious} 
        disabled={isUploading}
        size="large"
        shape="round"
      >
        Previous
      </Button>
    )}
  </div>
  
  <div>
    {currentStep < 2 ? (
      <Button
        type="primary"
        onClick={handleNext}
        disabled={isUploading || (currentStep === 0 && !csvFile)}
        loading={isUploading}
        size="large"
        shape="round"
      >
        {currentStep === 1 ? 'Import' : 'Next'}
      </Button>
    ) : (
      <div /> // Empty div for alignment on final step
    )}
  </div>
</ActionButtons>
  </div>
</DrawerComponent>

      {/* Confirmation Modal (you can reuse your DynamicConfirmationModal component here) */}
      {formCloseModel && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm</h5>
                <button type="button" className="close" onClick={() => setFormCloseModel(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to close? All mapping progress will be lost.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setFormCloseModel(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => {
                    setFormCloseModel(false);
                    setVisible(false);
                  }}
                >
                  Yes, Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkProductImport;